"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import type { CreateWaveConfig, CreateWaveOutcomeType, TimeWeightedVotingSettings } from "@/types/waves.types";
import { CreateWaveGroupConfigType, CreateWaveStep } from "@/types/waves.types";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { Time } from "@/helpers/time";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { ApiWaveCreditScope } from "@/generated/models/ApiWaveCreditScope";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import type { Period } from "../types/period";
import type { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { getCreateWaveValidationErrors } from "@/helpers/waves/create-wave.validation";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import { useMemeCardCount } from "./useMemeCardCount";
interface EndDateConfig {
  time: number | null;
  period: Period | null;
}
export function useWaveConfig() {
  const $ = _c(54);
  const initialType = ApiWaveType.Chat;
  const initialStep = CreateWaveStep.OVERVIEW;
  const getInitialConfig = _temp;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getInitialConfig({
      type: initialType
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [config, setConfig] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      time: null,
      period: null
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [endDateConfig, setEndDateConfig] = useState(t1);
  const [step, setStep] = useState(initialStep);
  const [selectedOutcomeType, setSelectedOutcomeType] = useState(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [errors, setErrors] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [groupsCache, setGroupsCache] = useState(t3);
  const shouldLoadMemeCount = config.voting.type === ApiWaveCreditType.CardSetTdh;
  let t4;
  if ($[4] !== shouldLoadMemeCount) {
    t4 = {
      enabled: shouldLoadMemeCount
    };
    $[4] = shouldLoadMemeCount;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const memeCountQuery = useMemeCardCount(t4);
  const memeCount = shouldLoadMemeCount && !memeCountQuery.isError ? memeCountQuery.data ?? null : null;
  let t5;
  bb0: {
    if (config.voting.creditNftMemeCount === memeCount) {
      t5 = config;
      break bb0;
    }
    let t6;
    if ($[6] !== config.voting || $[7] !== memeCount) {
      t6 = {
        ...config.voting,
        creditNftMemeCount: memeCount
      };
      $[6] = config.voting;
      $[7] = memeCount;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== config || $[10] !== t6) {
      t7 = {
        ...config,
        voting: t6
      };
      $[9] = config;
      $[10] = t6;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t5 = t7;
  }
  const effectiveConfig = t5;
  let t6;
  let t7;
  if ($[12] !== config.dates.endDate) {
    t6 = () => {
      if (config.dates.endDate === null) {
        setEndDateConfig({
          time: null,
          period: null
        });
      }
    };
    t7 = [config.dates.endDate];
    $[12] = config.dates.endDate;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setErrors([]);
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== config) {
    t9 = [config];
    $[16] = config;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] !== config.overview) {
    t10 = overview => {
      const isTypeChange = config.overview.type !== overview.type;
      if (isTypeChange) {
        setEndDateConfig({
          time: null,
          period: null
        });
      }
      setConfig(prev => {
        if (prev.overview.type === overview.type) {
          return {
            ...prev,
            overview
          };
        }
        return {
          ...getInitialConfig({
            type: overview.type
          }),
          overview
        };
      });
    };
    $[18] = config.overview;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const setOverview = t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = dates => {
      setConfig(prev_0 => ({
        ...prev_0,
        dates
      }));
    };
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const setDates = t11;
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = drops => {
      setConfig(prev_1 => ({
        ...prev_1,
        drops
      }));
    };
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const setDrops = t12;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = adminCanDeleteDrops => {
      setConfig(prev_2 => ({
        ...prev_2,
        drops: {
          ...prev_2.drops,
          adminCanDeleteDrops
        }
      }));
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const setDropsAdminCanDelete = t13;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = outcomes => {
      setConfig(prev_3 => ({
        ...prev_3,
        outcomes
      }));
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const setOutcomes = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = display => {
      setConfig(prev_4 => ({
        ...prev_4,
        display
      }));
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const setDisplay = t15;
  let t16;
  if ($[25] !== effectiveConfig || $[26] !== step) {
    t16 = t17 => {
      const {
        step: newStep,
        direction
      } = t17;
      if (direction === "forward") {
        const newErrors = getCreateWaveValidationErrors({
          config: effectiveConfig,
          step
        });
        if (newErrors.length) {
          setErrors(newErrors);
          return;
        }
      }
      setErrors([]);
      setSelectedOutcomeType(null);
      setStep(newStep);
    };
    $[25] = effectiveConfig;
    $[26] = step;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const onStep = t16;
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = outcomeType => {
      setSelectedOutcomeType(outcomeType);
      setErrors([]);
    };
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const onOutcomeTypeChange = t17;
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = t19 => {
      const {
        group,
        groupType
      } = t19;
      if (group) {
        setGroupsCache(prev_5 => ({
          ...prev_5,
          [group.id]: group
        }));
      }
      bb63: switch (groupType) {
        case CreateWaveGroupConfigType.CAN_VIEW:
          {
            setConfig(prev_10 => ({
              ...prev_10,
              groups: {
                ...prev_10.groups,
                canView: group?.id ?? null
              }
            }));
            break bb63;
          }
        case CreateWaveGroupConfigType.CAN_DROP:
          {
            setConfig(prev_9 => ({
              ...prev_9,
              groups: {
                ...prev_9.groups,
                canDrop: group?.id ?? null
              }
            }));
            break bb63;
          }
        case CreateWaveGroupConfigType.CAN_VOTE:
          {
            setConfig(prev_8 => ({
              ...prev_8,
              groups: {
                ...prev_8.groups,
                canVote: group?.id ?? null
              }
            }));
            break bb63;
          }
        case CreateWaveGroupConfigType.CAN_CHAT:
          {
            setConfig(prev_7 => ({
              ...prev_7,
              groups: {
                ...prev_7.groups,
                canChat: group?.id ?? null
              }
            }));
            break bb63;
          }
        case CreateWaveGroupConfigType.ADMIN:
          {
            setConfig(prev_6 => ({
              ...prev_6,
              groups: {
                ...prev_6.groups,
                admin: group?.id ?? null
              }
            }));
            break bb63;
          }
        default:
          {
            assertUnreachable(groupType);
          }
      }
    };
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const onGroupSelect = t18;
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = type_0 => {
      setConfig(prev_11 => ({
        ...prev_11,
        voting: {
          type: type_0,
          creditScope: prev_11.voting.creditScope,
          category: null,
          profileId: null,
          creditNfts: type_0 === ApiWaveCreditType.CardSetTdh ? prev_11.voting.creditNfts : [],
          creditNftMemeCount: null,
          allowNegativeVotes: prev_11.voting.allowNegativeVotes,
          maxVotesPerIdentityPerDrop: prev_11.voting.maxVotesPerIdentityPerDrop,
          winningThreshold: prev_11.voting.winningThreshold,
          timeWeighted: prev_11.voting.timeWeighted
        }
      }));
    };
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const onVotingTypeChange = t19;
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = timeWeighted => {
      setConfig(prev_12 => ({
        ...prev_12,
        voting: {
          ...prev_12.voting,
          timeWeighted
        }
      }));
    };
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const onTimeWeightedVotingChange = t20;
  let t21;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = enabled => {
      setConfig(prev_13 => ({
        ...prev_13,
        chat: {
          enabled
        }
      }));
    };
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  const onChatEnabledChange = t21;
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = category => {
      setConfig(prev_14 => ({
        ...prev_14,
        voting: {
          ...prev_14.voting,
          category
        }
      }));
    };
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const onCategoryChange = t22;
  let t23;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = profileId => {
      setConfig(prev_15 => ({
        ...prev_15,
        voting: {
          ...prev_15.voting,
          profileId
        }
      }));
    };
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  const onProfileIdChange = t23;
  let t24;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = creditNfts => {
      setConfig(prev_16 => ({
        ...prev_16,
        voting: {
          ...prev_16.voting,
          creditNfts
        }
      }));
    };
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  const onCreditNftsChange = t24;
  let t25;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = creditScope => {
      setConfig(prev_17 => ({
        ...prev_17,
        voting: {
          ...prev_17.voting,
          creditScope
        }
      }));
    };
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  const onCreditScopeChange = t25;
  let t26;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = maxVotesPerIdentityPerDrop => {
      setConfig(prev_18 => ({
        ...prev_18,
        voting: {
          ...prev_18.voting,
          maxVotesPerIdentityPerDrop
        }
      }));
    };
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  const onMaxVotesPerIdentityPerDropChange = t26;
  let t27;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = allowNegativeVotes => {
      setConfig(prev_19 => ({
        ...prev_19,
        voting: {
          ...prev_19.voting,
          allowNegativeVotes
        }
      }));
    };
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  const onAllowNegativeVotesChange = t27;
  let t28;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = winningThreshold => {
      setConfig(prev_20 => ({
        ...prev_20,
        voting: {
          ...prev_20.voting,
          winningThreshold
        }
      }));
    };
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  const onWinningThresholdChange = t28;
  let t29;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = threshold => {
      setConfig(prev_21 => ({
        ...prev_21,
        approval: {
          ...prev_21.approval,
          threshold
        }
      }));
    };
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  const onThresholdChange = t29;
  let t30;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = thresholdTimeMs => {
      setConfig(prev_22 => ({
        ...prev_22,
        approval: {
          ...prev_22.approval,
          thresholdTimeMs
        }
      }));
    };
    $[41] = t30;
  } else {
    t30 = $[41];
  }
  const onThresholdTimeChange = t30;
  let t31;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = maxWinners => {
      setConfig(prev_23 => ({
        ...prev_23,
        approval: {
          ...prev_23.approval,
          maxWinners
        }
      }));
    };
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  const onApprovalMaxWinnersChange = t31;
  const t32 = shouldLoadMemeCount && memeCountQuery.isLoading;
  const t33 = shouldLoadMemeCount && memeCountQuery.isError;
  let t34;
  if ($[43] !== effectiveConfig || $[44] !== endDateConfig || $[45] !== errors || $[46] !== groupsCache || $[47] !== onStep || $[48] !== selectedOutcomeType || $[49] !== setOverview || $[50] !== step || $[51] !== t32 || $[52] !== t33) {
    t34 = {
      config: effectiveConfig,
      setConfig,
      endDateConfig,
      setEndDateConfig,
      step,
      selectedOutcomeType,
      errors,
      groupsCache,
      isMemeCountLoading: t32,
      isMemeCountError: t33,
      setOverview,
      setDates,
      setDrops,
      setDropsAdminCanDelete,
      setOutcomes,
      setDisplay,
      onStep,
      onOutcomeTypeChange,
      onGroupSelect,
      onVotingTypeChange,
      onCategoryChange,
      onProfileIdChange,
      onCreditNftsChange,
      onCreditScopeChange,
      onMaxVotesPerIdentityPerDropChange,
      onAllowNegativeVotesChange,
      onTimeWeightedVotingChange,
      onWinningThresholdChange,
      onThresholdChange,
      onThresholdTimeChange,
      onApprovalMaxWinnersChange,
      onChatEnabledChange
    };
    $[43] = effectiveConfig;
    $[44] = endDateConfig;
    $[45] = errors;
    $[46] = groupsCache;
    $[47] = onStep;
    $[48] = selectedOutcomeType;
    $[49] = setOverview;
    $[50] = step;
    $[51] = t32;
    $[52] = t33;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  return t34;
}
function _temp(t0) {
  const {
    type
  } = t0;
  const now = Time.currentMillis();
  return {
    overview: {
      type,
      name: "",
      image: null
    },
    groups: {
      canView: null,
      canDrop: null,
      canVote: null,
      canChat: null,
      admin: null
    },
    chat: {
      enabled: true
    },
    dates: {
      submissionStartDate: now,
      votingStartDate: now,
      endDate: type === ApiWaveType.Rank ? now : null,
      firstDecisionTime: now,
      subsequentDecisions: [],
      isRolling: false
    },
    drops: {
      noOfApplicationsAllowedPerParticipant: null,
      requiredTypes: [],
      requiredMetadata: [],
      submissionStrategy: null,
      terms: null,
      signatureRequired: false,
      adminCanDeleteDrops: true
    },
    voting: {
      type: ApiWaveCreditType.TdhPlusXtdh,
      creditScope: ApiWaveCreditScope.Wave,
      category: null,
      profileId: null,
      creditNfts: [],
      creditNftMemeCount: null,
      allowNegativeVotes: true,
      maxVotesPerIdentityPerDrop: null,
      winningThreshold: null,
      timeWeighted: {
        enabled: false,
        averagingInterval: 24,
        averagingIntervalUnit: "hours"
      }
    },
    outcomes: [],
    approval: {
      threshold: null,
      thresholdTimeMs: null,
      maxWinners: null
    },
    display: {
      approve: {
        approvalsTabLabel: "",
        approvedTabLabel: ""
      }
    }
  };
}